import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { McButton, McIcon, McSelect, McLoadingIndicator, McTextarea, McCard, McToolbar } from '@maersk-global/mds-react-wrapper';
import '@maersk-global/mds-components-core-textarea';
import '@maersk-global/mds-components-core-toolbar';
import '@maersk-global/mds-components-core-button';
import '@maersk-global/mds-components-core-input';
import '@maersk-global/mds-components-core-avatar';
import { EXPERT_PROMPT_CATEGORIES } from '../constants/prompts';
import { TerminalSelect } from './Common/TerminalSelect';
import { DatabaseSelect } from './Common/DatabaseSelect';
import { DatabaseType } from '../utils/terminalConfig';
import type { AgentTestChatProfile } from '../utils/agentTestChatProfile';
import { getModelSpec, DEFAULT_MODEL } from '../config/modelRegistry';

// Approximate token count: ~4 chars per token (GPT heuristic)
const estimateTokens = (text: string) => Math.ceil(text.length / 4);

const MAX_CONTEXT_MESSAGES = 20;

const formatTokens = (n: number) =>
  n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);

/** Small SVG circular progress ring */
const ContextRing: React.FC<{ percent: number; size?: number }> = ({ percent, size = 32 }) => {
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(percent, 100) / 100) * circumference;
  const color =
    percent > 80
      ? 'var(--mds-color-feedback-error, #d63b30)'
      : percent > 50
        ? 'var(--mds-color-feedback-warning, #e68a00)'
        : 'var(--mds-color-brand-moss-100, #00754a)';
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke="var(--mds-color-neutral-weak-decoration, #e8eaed)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.4s ease' }} />
    </svg>
  );
};

interface FloatingAssistantProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  hideLauncher?: boolean;
  chatHistory: any[];
  setChatHistory: (v: any[]) => void;
  aiIntent: string;
  setAiIntent: (v: string) => void;
  aiDomainOverride: string;
  setAiDomainOverride: (v: string) => void;
  aiDomainOptions: any[];
  aiModelName?: string;
  setAiModelName?: (v: string) => void;
  aiModelOptions?: any[];
  aiGenerateMutation: any;
  handleAiGenerate: () => void;
  onRetryMessage?: (message: string) => void;
  chatScrollRef: React.RefObject<HTMLDivElement | null>;
  agentId?: string;
  agentName?: string;
  selectedTerminal: string;
  setSelectedTerminal: (v: string) => void;
  databaseType: DatabaseType;
  setDatabaseType: (v: DatabaseType) => void;
  testResponseType?: 'PLAINTEXT' | 'MARKDOWN' | 'JSON';
  setTestResponseType?: (v: 'PLAINTEXT' | 'MARKDOWN' | 'JSON') => void;
  /** When testing a saved agent, drives labels / DB selectors (e.g. GitHub vs SQL). */
  testChatProfile?: AgentTestChatProfile;
  /** Callback to start a new chat session (resets sessionId + history). */
  onNewChat?: () => void;
}

const FloatingAssistant: React.FC<FloatingAssistantProps> = ({
  open,
  onOpen,
  onClose,
  chatHistory,
  setChatHistory,
  aiIntent,
  setAiIntent,
  aiDomainOverride,
  setAiDomainOverride,
  aiDomainOptions,
  aiModelName,
  setAiModelName,
  aiModelOptions,
  aiGenerateMutation,
  handleAiGenerate,
  onRetryMessage,
  chatScrollRef,
  agentId,
  agentName,
  selectedTerminal,
  setSelectedTerminal,
  databaseType,
  setDatabaseType,
  testResponseType,
  setTestResponseType,
  testChatProfile,
  hideLauncher = false,
  onNewChat,
}) => {
  const [showPromptsInside, setShowPromptsInside] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);
  const [showContextDetail, setShowContextDetail] = useState(false);

  // ── Context-window usage calculation (driven by model registry) ──
  const modelSpec = React.useMemo(() => getModelSpec(aiModelName || DEFAULT_MODEL.value), [aiModelName]);
  const messageTokens = React.useMemo(
    () => chatHistory.reduce((sum, m) => sum + estimateTokens(String(m.content ?? '')), 0),
    [chatHistory]
  );
  const totalTokens = modelSpec.systemPromptTokens + modelSpec.toolDefinitionTokens + messageTokens;
  const contextPercent = Math.round((totalTokens / modelSpec.contextWindow) * 100);

  const handleCopyMessage = async (content: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(idx);
      window.setTimeout(() => setCopiedMessageIndex(null), 1500);
    } catch {
      // no-op fallback: clipboard may be blocked by browser permissions
    }
  };

  const handleRetryMessage = (content: string) => {
    if (onRetryMessage) {
      onRetryMessage(content);
      return;
    }
    setAiIntent(content);
  };

  const isAgentTestChat = Boolean(agentId);
  const resolvedAgentProfile: AgentTestChatProfile | undefined = isAgentTestChat
    ? (testChatProfile ?? {
          mode: 'sql',
          emptyStateMessage: "I'm your SQL Assistant. Ask me to help you build or explain your query.",
          inputLabel: 'What query do you want to build?',
          showDatabaseTerminalSelectors: true,
      })
    : undefined;

  const promptLibraryCategories =
      resolvedAgentProfile?.promptCategories ?? EXPERT_PROMPT_CATEGORIES;

  const normalizeSelectOptions = React.useCallback((options: any[]) => {
    return options
      .map((opt: any) => {
        const value = String(opt?.value ?? opt?.id ?? '').trim();
        const label = String(opt?.label ?? opt?.text ?? opt?.name ?? value).trim();
        return value ? { ...opt, value, label } : null;
      })
      .filter(Boolean) as Array<{ value: string; label: string }>;
  }, []);

  const resolvedDomainOptions = React.useMemo(
    () => normalizeSelectOptions(Array.isArray(aiDomainOptions) ? aiDomainOptions : []),
    [aiDomainOptions, normalizeSelectOptions]
  );

  const resolvedModelOptions = React.useMemo(
    () => normalizeSelectOptions(Array.isArray(aiModelOptions) ? aiModelOptions : []),
    [aiModelOptions, normalizeSelectOptions]
  );

  const resolvedDomainValue = React.useMemo(() => {
    const requested = aiDomainOverride || '';
    if (requested && resolvedDomainOptions.some((opt: any) => String(opt?.value ?? '') === requested)) {
      return requested;
    }
    return String(resolvedDomainOptions[0]?.value ?? '');
  }, [aiDomainOverride, resolvedDomainOptions]);

  const resolvedModelValue = React.useMemo(() => {
    const requested = aiModelName || '';
    if (requested && resolvedModelOptions.some((opt: any) => String(opt?.value ?? '') === requested)) {
      return requested;
    }
    return String(resolvedModelOptions[0]?.value ?? '');
  }, [aiModelName, resolvedModelOptions]);

  useEffect(() => {
    if (!agentId && resolvedDomainValue && resolvedDomainValue !== aiDomainOverride) {
      setAiDomainOverride(resolvedDomainValue);
    }
  }, [agentId, aiDomainOverride, resolvedDomainValue, setAiDomainOverride]);

  useEffect(() => {
    if (!agentId && setAiModelName && resolvedModelValue && resolvedModelValue !== aiModelName) {
      setAiModelName(resolvedModelValue);
    }
  }, [agentId, aiModelName, resolvedModelValue, setAiModelName]);

  const [showDomainPopover, setShowDomainPopover] = useState(false);
  const domainPopoverRef = useRef<HTMLDivElement>(null);

  // Close domain popover on outside click
  useEffect(() => {
    if (!showDomainPopover) return;
    const handleClick = (e: MouseEvent) => {
      if (domainPopoverRef.current && !domainPopoverRef.current.contains(e.target as Node)) {
        setShowDomainPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDomainPopover]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Floating icon-only button
  if (!open) {
    if (hideLauncher) {
      return null;
    }

    return (
      <div
        style={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(225, 30, 30, 0.15)',
          borderRadius: '50%',
          background: 'var(--mds-color-brand-moss-100)',
          color: 'var(--mds-color-neutral-white)',
          cursor: 'pointer',
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        onClick={onOpen}
        aria-label="Open AI Assistant"
      >
        <McIcon icon="robot" style={{ fontSize: 24 }} size='24' />
      </div>
    );
  }

  // Expanded panel
  return (
    <div style={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 9999,
      width: 800,
      maxWidth: '90vw',
      backgroundColor: 'var(--mds-color-neutral-white, #ffffff)',
      borderRadius: 16,
      boxShadow: '0 8px 32px rgba(255, 255, 255, 0.18)',
      border: '1px solid var(--mds-color-neutral-weak-decoration, #e8eaed)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: 700,
      maxHeight: '80vh',
    }}>
      <McToolbar style={{
        backgroundColor: 'var(--mds-color-neutral-white, #ffffff)',
        color: 'var(--mds-color-neutral-black, #000000)',
        padding: '12px 20px',
        borderBottom: '1px solid var(--mds-color-neutral-weak-decoration, #e8eaed)',
        borderRadius: '16px 16px 0 0',
      } as any}>
        <div slot="start" style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 18 }}>
          <McIcon icon={agentId ? "robot" : "robot"} style={{ fontSize: 20 }} />
          {agentId ? `Testing Agent: ${agentName || 'Agent'}` : 'AI Assistant'}
        </div>
        <div slot="end" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <McIcon
            icon={showPromptsInside ? "comment-dots" : "star"}
            style={{ fontSize: 20, cursor: 'pointer', opacity: 0.8, transition: 'opacity 0.2s' }}
            onClick={() => setShowPromptsInside(!showPromptsInside)}
            onMouseEnter={(e: any) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e: any) => e.currentTarget.style.opacity = '0.8'}
            aria-label={showPromptsInside ? "Show Chat" : "Show Prompts"}
          />
          {/* Context Window indicator */}
          <div
            style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={() => setShowContextDetail((v) => !v)}
            title="Context Window Usage"
          >
            <ContextRing percent={contextPercent} size={28} />
            <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, minWidth: 28, textAlign: 'center' }}>
              {contextPercent}%
            </span>
            {showContextDetail && (
              <div style={{
                position: 'absolute', top: 36, right: 0, zIndex: 10,
                background: 'var(--mds-color-neutral-white, #fff)',
                border: '1px solid var(--mds-color-neutral-weak-decoration, #e8eaed)',
                borderRadius: 12, padding: '14px 18px', minWidth: 220,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                fontSize: 13, lineHeight: 1.7,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14 }}>Context Window</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontWeight: 600 }}>{formatTokens(totalTokens)} / {formatTokens(modelSpec.contextWindow)} tokens</span>
                  <span style={{ fontWeight: 600 }}>{contextPercent}%</span>
                </div>
                <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>Model: {modelSpec.label}</div>
                <div style={{
                  height: 6, borderRadius: 3, marginTop: 4, marginBottom: 10,
                  background: 'var(--mds-color-neutral-weak-decoration, #e8eaed)', overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min(contextPercent, 100)}%`, height: '100%', borderRadius: 3,
                    background: contextPercent > 80 ? 'var(--mds-color-feedback-error, #d63b30)'
                      : contextPercent > 50 ? 'var(--mds-color-feedback-warning, #e68a00)'
                        : 'var(--mds-color-brand-moss-100, #00754a)',
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <div style={{ borderTop: '1px solid var(--mds-color-neutral-weak-decoration, #e8eaed)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2, opacity: 0.8 }}>System</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>System Instructions</span>
                    <span>{((modelSpec.systemPromptTokens / modelSpec.contextWindow) * 100).toFixed(1)}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Tool Definitions</span>
                    <span>{((modelSpec.toolDefinitionTokens / modelSpec.contextWindow) * 100).toFixed(1)}%</span>
                  </div>
                </div>
                <div style={{ borderTop: '1px solid var(--mds-color-neutral-weak-decoration, #e8eaed)', paddingTop: 8, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2, opacity: 0.8 }}>User Context</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Messages ({chatHistory.length}/{MAX_CONTEXT_MESSAGES})</span>
                    <span>{((messageTokens / modelSpec.contextWindow) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <McIcon
            icon="times-circle"
            style={{ fontSize: 20, cursor: 'pointer', opacity: 0.8, transition: 'opacity 0.2s' }}
            onClick={onClose}
            onMouseEnter={(e: any) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e: any) => e.currentTarget.style.opacity = '0.8'}
            aria-label="Close"
          />
        </div>
      </McToolbar>

      <div
        style={{
          padding: '10px 20px',
          backgroundColor: 'var(--mds-color-feedback-warning-weak-canvas, #fff7e6)',
          borderBottom: '1px solid var(--mds-color-feedback-warning-strong-decoration, #e0a100)',
          borderLeft: '4px solid var(--mds-color-feedback-warning-strong-decoration, #e0a100)',
          color: 'var(--mds-color-neutral-strong-text, #1f2937)',
          fontSize: 12,
          lineHeight: 1.45,
          fontWeight: 500,
        }}
      >
        <span style={{ fontWeight: 700 }}>Warning:</span> AI can make mistakes. Manually review responses, SQL, and suggested changes for accuracy and correctness before using them.
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0 }}>
        {showPromptsInside ? (
          /* Internal Prompt Library View */
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: 20,
            backgroundColor: 'var(--mds-color-neutral-weakest-background)',
            display: 'flex',
            flexDirection: 'column',
            gap: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, color: 'var(--mds-color-brand-moss-100, #00754a)' }}>Prompt Library</h3>
              <McIcon icon='arrow-curved-up-left' onClick={() => setShowPromptsInside(false)} />
            </div>
            {promptLibraryCategories.map((category, idx) => (
              <div key={idx} style={{
                marginBottom: 16,
                backgroundColor: 'var(--mds-color-neutral-white, #ffffff)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid var(--mds-color-neutral-weak-decoration, #e8eaed)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <h4 style={{ margin: '0 0 6px 0', fontSize: 14, fontWeight: 700 }}>{category.title}</h4>
                <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--mds-color-neutral-weak-text)' }}>{category.description}</p>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: '1px solid var(--mds-color-neutral-weak-decoration, #e8eaed)',
                  margin: '0 -16px -16px -16px' // bleed to edges
                }}>
                  {category.prompts.map((prompt, pIdx) => (
                    <div
                      key={pIdx}
                      onClick={() => {
                        setAiIntent(prompt);
                        setShowPromptsInside(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        backgroundColor: 'transparent',
                        fontSize: 13,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        borderBottom: pIdx < category.prompts.length - 1 ? '1px solid #f1f3f4' : 'none',
                        lineHeight: 1.4
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--mds-color-neutral-weakest-background, #f5f5f5)';
                        e.currentTarget.style.color = 'var(--mds-color-brand-moss-100, #00754a)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'inherit';
                      }}
                    >
                      {prompt}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Chat history */
          <div ref={chatScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {chatHistory.length === 0 && (
              <div style={{
                textAlign: 'center',
                color: 'var(--mds-color-neutral-weaker-text)',
                marginTop: 60,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16
              }}>
                <McIcon icon="robot" style={{ fontSize: 56, opacity: 0.2 }} />
                <p style={{ fontSize: 15, maxWidth: '320px', margin: 0, lineHeight: 1.5 }}>
                  {isAgentTestChat && resolvedAgentProfile
                    ? resolvedAgentProfile.emptyStateMessage
                    : "I'm your SQL Assistant. Ask me to help you build or explain your query."}
                </p>
                <McButton appearance="secondary" fit="small" onClick={() => setShowPromptsInside(true)}>
                  Explore Prompt Library
                </McButton>
              </div>
            )}
            {chatHistory.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  backgroundColor: msg.role === 'user' ? 'var(--mds-color-brand-moss-100, #ebebebff)' : 'var(--mds-color-neutral-white, #ffffff)',
                  color: msg.role === 'user' ? 'var(--mds-color-neutral-black, #000000)' : 'var(--mds-color-neutral-strong-text, #202124)',
                  padding: '12px 42px 12px 16px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  border: msg.role === 'user' ? 'none' : '1px solid var(--mds-color-neutral-weak-decoration, #e8eaed)',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: aiGenerateMutation.isPending && msg.role === 'user' ? 'not-allowed' : 'pointer',
                    opacity: aiGenerateMutation.isPending && msg.role === 'user' ? 0.4 : 0.85,
                  }}
                  onClick={() => {
                    if (msg.role === 'assistant') {
                      handleCopyMessage(String(msg.content ?? ''), idx);
                      return;
                    }
                    if (msg.role === 'user' && !aiGenerateMutation.isPending) {
                      handleRetryMessage(String(msg.content ?? ''));
                    }
                  }}
                  title={msg.role === 'assistant' ? (copiedMessageIndex === idx ? 'Copied' : 'Copy to clipboard') : 'Retry this message'}
                >
                  <McIcon
                    icon={msg.role === 'assistant' ? 'copy' : 'rotate'}
                    style={{
                      fontSize: 14,
                      color: msg.role === 'assistant' && copiedMessageIndex === idx
                        ? 'var(--mds-color-brand-moss-100, #00754a)'
                        : 'var(--mds-color-neutral-weaker-text)',
                    }}
                  />
                </div>

                <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 4, opacity: 0.8 }}>
                  {msg.role === 'user' ? 'YOU' : (msg.agentName ? msg.agentName.toUpperCase() : (agentId ? (agentName || 'AGENT').toUpperCase() : 'EXPERT AGENT'))}
                </div>
                {msg.role === 'assistant' ? (
                  <div className="assistant-markdown" style={{ fontSize: 14, lineHeight: 1.6 }}>
                    <ReactMarkdown>{String(msg.content ?? '').replace(/\\n/g, '\n')}</ReactMarkdown>
                  </div>
                ) : (
                  <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{msg.content}</div>
                )}
              </div>
            ))}
            {aiGenerateMutation.isPending && (
              <div style={{
                alignSelf: 'flex-start',
                backgroundColor: 'var(--mds-color-neutral-weakest-background)',
                padding: '12px 16px',
                borderRadius: '14px 14px 14px 2px',
                display: 'flex',
                alignItems: 'center',
                gap: 10
              }}>
                <McLoadingIndicator fit="small" />
                <span style={{ fontSize: 13, fontWeight: 500 }}>Thinking...</span>
              </div>
            )}
          </div>
        )}

        {/* Footer Area with Controls and Input */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--mds-color-neutral-weak-decoration)',
          backgroundColor: 'var(--mds-color-neutral-white)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10
        }}>
          {!showPromptsInside && (
            <>
              <div style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--mds-color-neutral-weakest-background)',
                borderRadius: '12px',
                padding: '4px',
                border: '1px solid var(--mds-color-neutral-weak-decoration)',
                transition: 'border-color 0.2s',
              }}
                onFocusCapture={(e: any) => { e.currentTarget.style.borderColor = 'var(--mds-color-brand-moss-100)'; }}
                onBlurCapture={(e: any) => { e.currentTarget.style.borderColor = 'var(--mds-color-neutral-weak-decoration)'; }}
              >
                {isAgentTestChat && setTestResponseType && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(180px, 220px)',
                    gap: 8,
                    padding: '8px 8px 0',
                  }}>
                    <McSelect
                      label="Response format"
                      value={testResponseType || 'MARKDOWN'}
                      data={[
                        { label: 'Markdown', value: 'MARKDOWN' },
                        { label: 'Plain text', value: 'PLAINTEXT' },
                        { label: 'JSON', value: 'JSON' },
                      ]}
                      optionselected={(e: any) => setTestResponseType?.(e.detail.value)}
                      disabled={aiGenerateMutation.isPending}
                    />
                  </div>
                )}
                <McTextarea
                  label={
                    isAgentTestChat && resolvedAgentProfile
                      ? resolvedAgentProfile.inputLabel
                      : 'What query you want to build?  '
                  }
                  labelposition='top'
                  value={aiIntent}
                  input={(e: any) =>
                    setAiIntent(
                      e.detail?.value !== undefined && e.detail?.value !== null
                        ? String(e.detail.value)
                        : e.target?.value ?? ''
                    )
                  }
                  keydown={(e: any) => {
                    const key = e.detail?.key || e.key;
                    const shiftKey = e.detail?.shiftKey || e.shiftKey;
                    if (key === 'Enter' && !shiftKey && aiIntent.trim() && !aiGenerateMutation.isPending) {
                      if (e.preventDefault) e.preventDefault();
                      handleAiGenerate();
                    }
                  }}
                  disabled={aiGenerateMutation.isPending}
                  style={{ flex: 1, '--mds-textarea-background-color': 'transparent', '--mds-textarea-border-color': 'transparent' } as any}
                  rows={Math.min(6, Math.max(2, aiIntent.split('\n').filter(line => line.trim().length > 0 || line === '').length))}
                />
                {/* Bottom bar: pill chips + send button */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {/* Model segmented pills */}
                    {!agentId && (
                      <div style={{
                        display: 'inline-flex',
                        borderRadius: 20,
                        border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
                        overflow: 'hidden',
                      }}>
                        {resolvedModelOptions.map((opt: any) => {
                          const isActive = String(opt.value) === String(resolvedModelValue);
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setAiModelName?.(opt.value)}
                              disabled={aiGenerateMutation.isPending}
                              style={{
                                padding: '4px 12px',
                                fontSize: 11,
                                fontWeight: isActive ? 700 : 500,
                                border: 'none',
                                cursor: aiGenerateMutation.isPending ? 'default' : 'pointer',
                                backgroundColor: isActive
                                  ? 'var(--mds-color-brand-moss-100, #00754a)'
                                  : 'transparent',
                                color: isActive
                                  ? 'var(--mds-color-neutral-white, #fff)'
                                  : 'var(--mds-color-neutral-strong-text, #1f2937)',
                                transition: 'all 0.15s ease',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {/* Expert agent chip with popover */}
                    {!agentId && (
                      <div ref={domainPopoverRef} style={{ position: 'relative' }}>
                        <button
                          onClick={() => setShowDomainPopover((v) => !v)}
                          disabled={aiGenerateMutation.isPending}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                            border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
                            borderRadius: 20,
                            cursor: aiGenerateMutation.isPending ? 'default' : 'pointer',
                            backgroundColor: 'transparent',
                            color: 'var(--mds-color-neutral-strong-text, #1f2937)',
                            transition: 'all 0.15s ease',
                            whiteSpace: 'nowrap',
                            maxWidth: 200,
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {resolvedDomainOptions.find((o: any) => o.value === resolvedDomainValue)?.label || 'Expert Agent'}
                          </span>
                          <McIcon icon="chevron-down" style={{ fontSize: 12, flexShrink: 0 }} />
                        </button>
                        {showDomainPopover && (
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            marginBottom: 6,
                            zIndex: 20,
                            minWidth: 240,
                            maxHeight: 260,
                            overflowY: 'auto',
                            backgroundColor: 'var(--mds-color-neutral-white, #fff)',
                            border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
                            borderRadius: 10,
                            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                            padding: '4px 0',
                          }}>
                            {resolvedDomainOptions.map((opt: any) => {
                              const isActive = String(opt.value) === String(resolvedDomainValue);
                              return (
                                <div
                                  key={opt.value}
                                  onClick={() => { setAiDomainOverride(opt.value); setShowDomainPopover(false); }}
                                  style={{
                                    padding: '8px 14px',
                                    fontSize: 12,
                                    fontWeight: isActive ? 700 : 400,
                                    cursor: 'pointer',
                                    backgroundColor: isActive
                                      ? 'var(--mds-color-primary-weakest-canvas, #e8f5e9)'
                                      : 'transparent',
                                    color: isActive
                                      ? 'var(--mds-color-brand-moss-100, #00754a)'
                                      : 'var(--mds-color-neutral-strong-text, #1f2937)',
                                    transition: 'background-color 0.1s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                  }}
                                  onMouseEnter={(e: any) => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--mds-color-neutral-weakest-background, #f8f9fa)'; }}
                                  onMouseLeave={(e: any) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                  {isActive && <McIcon icon="check-circle" style={{ fontSize: 14 }} />}
                                  {opt.label}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Agent test: database + terminal selectors (keep as-is) */}
                    {agentId && resolvedAgentProfile?.showDatabaseTerminalSelectors && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <DatabaseSelect
                          label=""
                          value={databaseType}
                          onChange={setDatabaseType}
                          disabled={aiGenerateMutation.isPending}
                          style={{ '--mds-select-height': '28px' } as any}
                        />
                        <TerminalSelect
                          label=""
                          value={selectedTerminal}
                          onChange={setSelectedTerminal}
                          disabled={aiGenerateMutation.isPending}
                          style={{ '--mds-select-height': '28px' } as any}
                        />
                      </div>
                    )}
                    {/* New Chat chip */}
                    {chatHistory.length > 0 && (
                      <button
                        onClick={() => { if (onNewChat) { onNewChat(); } else { setChatHistory([]); setAiIntent(''); } }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 10px',
                          fontSize: 11,
                          fontWeight: 600,
                          border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
                          borderRadius: 20,
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          color: 'var(--mds-color-neutral-strong-text, #1f2937)',
                          transition: 'all 0.15s ease',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <McIcon icon="plus" style={{ fontSize: 12 }} />
                        New Chat
                      </button>
                    )}
                  </div>
                  {/* Send button */}
                  <McIcon
                    icon="paper-plane"
                    onClick={() => { if (aiIntent.trim() && !aiGenerateMutation.isPending) handleAiGenerate(); }}
                    style={{
                      fontSize: 20,
                      cursor: aiIntent.trim() ? 'pointer' : 'default',
                      color: aiIntent.trim() ? 'var(--mds-color-brand-moss-100, #00754a)' : 'var(--mds-color-neutral-weaker-text)',
                      opacity: aiIntent.trim() ? 1 : 0.4,
                      transition: 'all 0.2s',
                      flexShrink: 0,
                    }}
                    aria-label="Send"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloatingAssistant;
